/**
 * API-Dokumentation Section — embed inside the Portal "API & Schnittstellen" tab.
 *
 * Shows:
 *   • Swagger UI (collapsible iframe) + ReDoc + OpenAPI JSON links
 *   • SDK-Downloads (Python / Node.js / PHP)
 *   • Endpoint-Referenz als zusammengefasste Tabelle
 *   • „Dokumentation drucken / als PDF speichern"
 *
 * When `enabled === false` the whole content is replaced by a friendly
 * "deactivated by platform admin" notice so the Tab still renders cleanly.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  BookOpen, Code2, FileJson, Download, ExternalLink, ChevronDown, ChevronUp,
  Printer, ShieldAlert, Webhook, Rocket, Terminal,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SDKS = [
  { lang: 'python', label: 'Python', icon: '🐍', hint: 'requests ≥ 2.28 · Python ≥ 3.8' },
  { lang: 'nodejs', label: 'Node.js', icon: '⬢', hint: 'axios ≥ 1.6 · Node ≥ 16' },
  { lang: 'php', label: 'PHP', icon: '🐘', hint: 'Guzzle 7 · PHP ≥ 7.4' },
];

const ENDPOINTS = [
  { method: 'GET', path: '/api/v1/materials', purpose: 'Materialien auflisten (paginiert)', scope: 'materials:read' },
  { method: 'POST', path: '/api/v1/materials', purpose: 'Material anlegen / upserten', scope: 'materials:write' },
  { method: 'GET', path: '/api/v1/wanted', purpose: 'Bedarfe auflisten', scope: 'wanted:read' },
  { method: 'POST', path: '/api/v1/wanted', purpose: 'Bedarf anlegen / upserten', scope: 'wanted:write' },
  { method: 'GET', path: '/api/v1/orders', purpose: 'Bestellungen auflisten', scope: 'orders:read' },
  { method: 'PATCH', path: '/api/v1/orders/{id}', purpose: 'Status / Tracking aktualisieren', scope: 'orders:write' },
  { method: 'POST', path: '/api/v1/matching/search', purpose: 'Matching-Suche ausführen', scope: 'materials:read' },
  { method: 'POST', path: '/api/v1/oauth/token', purpose: 'OAuth 2.0 Client-Credentials (JWT)', scope: '—' },
  { method: 'GET', path: '/api/v1/sandbox/*', purpose: 'Isolierte Sandbox-Endpoints (Demo-Daten)', scope: '—' },
];

const METHOD_STYLES = {
  GET: 'bg-emerald-100 text-emerald-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-amber-100 text-amber-700',
  PATCH: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
};

export default function ApiDocsSection({ enabled = true }) {
  const [swaggerOpen, setSwaggerOpen] = useState(false);

  if (!enabled) {
    return (
      <Card data-testid="api-docs-disabled-card" className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-slate-500" />
            API-Dokumentation
          </CardTitle>
          <CardDescription>
            Die Schnittstellen-Dokumentation wurde für diese Gruppe bzw. Site vom Plattform-Administrator deaktiviert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Ihre Tokens &amp; Datenabrufe funktionieren weiterhin. Bitte wenden Sie sich an den Plattform-Admin, wenn Sie
            Zugriff auf Swagger UI, SDKs und die Endpoint-Referenz benötigen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="api-docs-section" className="border-orange-200/60">
      <CardHeader className="pb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-orange-500" />
              API-Dokumentation
              <Badge className="bg-orange-100 text-orange-700 font-mono text-[10px]">v1.0</Badge>
            </CardTitle>
            <CardDescription>
              OpenAPI 3.1 · Swagger UI · ReDoc · SDKs (Python / Node.js / PHP) · Beispiel-Aufrufe
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            data-testid="api-docs-print-btn"
            className="shrink-0"
          >
            <Printer className="h-4 w-4 mr-2" /> Drucken / PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick-Links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="api-docs-quicklinks">
          <QuickLinkCard
            href={`${API_BASE}/v1/docs`}
            icon={BookOpen}
            title="Swagger UI"
            subtitle="Interaktiv · Try-it-out"
            testid="api-docs-swagger-link"
          />
          <QuickLinkCard
            href={`${API_BASE}/v1/redoc`}
            icon={BookOpen}
            title="ReDoc"
            subtitle="Schöne Lese-Referenz"
            testid="api-docs-redoc-link"
          />
          <QuickLinkCard
            href={`${API_BASE}/v1/openapi.json`}
            icon={FileJson}
            title="OpenAPI 3.1"
            subtitle="JSON herunterladen"
            testid="api-docs-openapi-link"
          />
        </div>

        {/* SDK Downloads */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Rocket className="h-4 w-4 text-orange-500" /> Offizielle SDKs
            </h3>
            <a href="/developers" className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1">
              Developer-Portal <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="api-docs-sdk-grid">
            {SDKS.map((s) => (
              <div
                key={s.lang}
                className="border rounded-lg p-3 flex items-center justify-between hover:border-orange-300 transition"
                data-testid={`api-docs-sdk-${s.lang}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl" aria-hidden>{s.icon}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{s.hint}</p>
                  </div>
                </div>
                <a href={`${API_BASE}/v1/sdks/${s.lang}`} download data-testid={`api-docs-sdk-download-${s.lang}`}>
                  <Button size="sm" variant="outline">
                    <Download className="h-3.5 w-3.5 mr-1" /> ZIP
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Endpoint-Tabelle */}
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Code2 className="h-4 w-4 text-orange-500" /> Endpoint-Übersicht
          </h3>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm" data-testid="api-docs-endpoints-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b bg-muted/30">
                  <th className="py-2 px-3 w-20">Methode</th>
                  <th className="py-2 px-3">Pfad</th>
                  <th className="py-2 px-3">Zweck</th>
                  <th className="py-2 px-3 hidden md:table-cell">Scope</th>
                </tr>
              </thead>
              <tbody>
                {ENDPOINTS.map((e) => (
                  <tr key={`${e.method}-${e.path}`} className="border-b last:border-0 hover:bg-muted/20" data-testid={`endpoint-${e.method}-${e.path}`}>
                    <td className="py-2 px-3">
                      <Badge className={`${METHOD_STYLES[e.method] || 'bg-slate-100 text-slate-700'} text-[10px] font-mono`}>
                        {e.method}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{e.path}</td>
                    <td className="py-2 px-3 text-xs">{e.purpose}</td>
                    <td className="py-2 px-3 font-mono text-[11px] hidden md:table-cell">{e.scope}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Beispiel-Aufruf */}
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Terminal className="h-4 w-4 text-orange-500" /> Beispiel-Aufruf (Bearer-Token)
          </h3>
          <pre
            className="bg-slate-950 text-slate-100 text-xs p-4 rounded-lg overflow-x-auto"
            data-testid="api-docs-curl-sample"
          >
{`curl -X GET "${window.location.origin}/api/v1/materials?limit=50" \\
  -H "Authorization: Bearer sfy_live_<IHR_TOKEN>"`}
          </pre>
        </div>

        {/* Webhooks Hinweis */}
        <div className="flex items-start gap-3 p-3 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 rounded-lg" data-testid="api-docs-webhooks-hint">
          <Webhook className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Webhooks &amp; HMAC-Signaturen</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Subscribe auf <code className="font-mono">order.*</code>, <code className="font-mono">product.*</code>,{' '}
              <code className="font-mono">subscription.*</code>. Alle Payloads sind HMAC-SHA256 signiert (Header
              <code className="font-mono"> X-Surplify-Signature</code>). Konfiguration unterhalb im Tab „Webhooks".
            </p>
          </div>
        </div>

        {/* Swagger UI Embed (collapsible) */}
        <div className="border rounded-lg overflow-hidden" data-testid="api-docs-swagger-embed-wrapper">
          <button
            type="button"
            onClick={() => setSwaggerOpen((o) => !o)}
            className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition"
            data-testid="api-docs-swagger-toggle"
          >
            <span className="text-sm font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-orange-500" />
              Swagger UI direkt einbetten {swaggerOpen ? '' : '(ausgeblendet)'}
            </span>
            {swaggerOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {swaggerOpen && (
            <iframe
              title="Swagger UI"
              src={`${API_BASE}/v1/docs`}
              className="w-full h-[720px] border-0 bg-white"
              data-testid="api-docs-swagger-iframe"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function QuickLinkCard({ href, icon: Icon, title, subtitle, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="border rounded-lg p-3 flex items-center gap-3 hover:border-orange-300 hover:shadow-sm transition group"
      data-testid={testid}
    >
      <div className="h-9 w-9 rounded-md bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-orange-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>
      </div>
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-orange-500" />
    </a>
  );
}
